import React from 'react';
import { Link } from 'react-router-dom';
import heroImage from 'url:../../../assets/main-photo.jpg';
import founderPhoto from 'url:../../../assets/prayrit.jpeg';
import { getRoutePaths } from '../../utils/i18n';
import { useTranslations } from '../../utils/translations';
import { useLanguage } from '../../utils/LanguageContext';
import AcrossChaptersMap from '../AcrossChaptersMap/AcrossChaptersMap';
import ArrowIcon from '../ArrowIcon/ArrowIcon';
import './Main.css';

const Main = () => {
  const { currentLang } = useLanguage();
  const { t } = useTranslations();
  const paths = getRoutePaths(currentLang);
  
  // Get disciplines from translations
  const disciplines = t('home', 'hero.disciplines', [
    'AI', 'Digitalisation', 'Process optimisation', 'Web development', 'Automation', 'Graphic design', 'Branding'
  ]);
  
  // Get capabilities list from translations
  const capabilitiesList = t('home', 'capabilities.list', [
    'finance', 'web development', 'graphic design', 'ai & automation', 'communication', 
    'digitalisation', 'creative development', 'process optimisation', 'branding & identity'
  ]);

  return (
    <>
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-hero-strip">
          <span>{t('home', 'hero.strip.establish')} {t('home', 'hero.strip.year')} </span>
          <span className="home-hero-strip-divider" aria-hidden="true" />
          <Link to={paths.principles} className="home-hero-strip-link">
            {t('home', 'hero.strip.principlesLink')} <ArrowIcon />
          </Link>
        </div>
        <div className="home-hero-text">
          <h1 className={`home-title${currentLang === 'de' ? ' home-title--de' : ''}`} id="home-title">
            {(() => {
              const title = t('home', 'hero.title');
              const lastSpace = title.lastIndexOf(' ');
              if (lastSpace === -1) return title;
              return <>{title.slice(0, lastSpace + 1)}<span className="home-title-accent">{title.slice(lastSpace + 1)}</span></>;
            })()}
          </h1>
          <p className="home-script">{t('home', 'hero.script')}</p>
          <p className="home-locations">
            <span>{t('home', 'hero.locations')}</span>
          </p>
          <ul className="home-disciplines" aria-label={t('common', 'capabilities', 'Core capabilities') || 'Core capabilities'}>
            {disciplines.map((discipline, index) => (
              <li key={discipline}><span>0{index + 1}</span>{discipline}</li>
            ))}
          </ul>
          <div className="home-actions">
            <Link to={paths.services} className="home-button home-button-primary">
              {t('home', 'hero.actions.seeServices')} <ArrowIcon />
            </Link>
            <Link to={paths.contact} className="home-button">
              {t('home', 'hero.actions.startProject')} <ArrowIcon />
            </Link>
          </div>
        </div>
        <div className="home-hero-image">
          <img
            src={heroImage}
            alt="thewhizkind main visual"
            width="800"
            height="1000"
            decoding="async"
            fetchpriority="high"
          />
          <span className="hero-register hero-register-top" aria-hidden="true" />
          <span className="hero-register hero-register-bottom" aria-hidden="true" />
          <span className="hero-scan" aria-hidden="true" />
          <div className="hero-system" aria-hidden="true">
            <span className="hero-system-status"><i />system Online</span>
            <span><b>mode</b> {t('home', 'hero.system.mode')}</span>
            <span><b>nodes</b> {t('home', 'hero.locations')}</span>
            <span><b>build</b> {t('home', 'hero.system.build')}</span>
          </div>
        </div>
      </section>

      <section className="home-capabilities" aria-labelledby="capabilities-title">
        <div className="home-capabilities-aside">
          <span className="section-kicker">{t('home', 'capabilities.kicker')}</span>
          <Link to={paths.services} className="capabilities-about-link">
            {t('home', 'capabilities.exploreLink')} <ArrowIcon />
          </Link>
        </div>
        <div className="capability-axis" aria-hidden="true">
          <span>{t('home', 'capabilities.axis.thinking')}</span>
          <i><b /></i>
          <span>{t('home', 'capabilities.axis.making')}</span>
        </div>
        <div className="home-capabilities-heading">
          <h2 id="capabilities-title">
            <span>{t('home', 'capabilities.title')[0]}</span>
            <span>{t('home', 'capabilities.title')[1]}</span>
          </h2>
          <p className="home-capabilities-lede">
            {t('home', 'capabilities.lede')}
          </p>
        </div>
        <ul aria-label={t('common', 'capabilities', 'Capabilities') || 'Capabilities'}>
          {capabilitiesList.map((capability, index) => (
            <li key={capability}>{capability}</li>
          ))}
        </ul>
        <span className="capability-mark" aria-hidden="true">+</span>
      </section>

      <section className="work-portal" id="selected-work" aria-labelledby="work-portal-title">
        <Link to={paths.gallery} className="work-portal-link">
          <span className="work-portal-kicker">{t('home', 'workPortal.kicker')}</span>
          <h2 id="work-portal-title">
            {t('home', 'workPortal.title').split('/').map((part, i) => (
              <React.Fragment key={i}>{i > 0 && <br />}{part.trim()}</React.Fragment>
            ))}
          </h2>
          <span className="work-portal-arrow" aria-hidden="true"><ArrowIcon /></span>
          <p>{t('home', 'workPortal.description')}</p>
          <div className="work-portal-signal" aria-hidden="true">
            <span /><span /><span /><span /><span /><span /><span />
          </div>
        </Link>
      </section>

      <section className="founder-intro" aria-labelledby="founder-intro-title">
        <figure className="founder-intro-portrait">
          <div className="founder-intro-portrait-frame">
            <img
              src={founderPhoto}
              alt="Prayrit, founder of thewhizkind"
              width="480"
              height="640"
              loading="lazy"
              decoding="async"
            />
            <figcaption className="founder-intro-caption">{currentLang === 'de' ? 'porträt' : 'portrait'} / frankfurt</figcaption>
          </div>
        </figure>
        <div className="founder-intro-text">
          <span className="section-kicker">{t('home', 'founder.kicker')}</span>
          <h2 id="founder-intro-title">
            {(() => {
              const title = t('home', 'founder.title');
              const parts = title.split('prayrit');
              return <>{parts[0]}<em>prayrit</em>{parts.slice(1).join('prayrit')}</>;
            })()}
          </h2>
          <p>
            {t('home', 'founder.bio')}
          </p>
          <div className="founder-intro-meta">
            {t('home', 'founder.meta').map((meta, index) => (
              <span key={meta}>{meta}</span>
            ))}
          </div>
          <Link to={paths.about} className="founder-intro-link">
            {t('home', 'founder.moreLink')} <ArrowIcon />
          </Link>
        </div>
      </section>

      <section className="home-contact-cta" aria-labelledby="home-contact-title">
        <span className="section-kicker">{t('home', 'contactCta.kicker')}</span>
        <h2 id="home-contact-title" className={currentLang === 'de' ? 'home-contact-title--de' : undefined}>
          <span>{t('home', 'contactCta.title')[0]}</span>
          <span className="contact-title-emphasis">
            {(() => {
              const subtitle = t('home', 'contactCta.title')[1];
              const lastSpace = subtitle.lastIndexOf(' ');
              if (lastSpace === -1) return <em>{subtitle}</em>;
              return <>{subtitle.slice(0, lastSpace + 1)}<em>{subtitle.slice(lastSpace + 1)}</em></>;
            })()}
          </span>
        </h2>
        <Link to={paths.contact} className="home-contact-link">
          {t('home', 'contactCta.link')} <ArrowIcon />
        </Link>
      </section>

      <AcrossChaptersMap className="home-map" />
    </>
  );
};

export default Main;
